import React, { useState } from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import config from 'config';
import { notifyGeneral, openGlobalDialog } from 'redux/actions'; // actions
import { getRouteMediafileDetail, getRouteArtworkCreator } from 'routes';
import {
	IconButton,
	Backdrop,
	CircularProgress,
	Menu,
	MenuItem,
	Typography,
	Link,
	Box,
} from '@mui/material';
import { LinkedIn, Instagram, Facebook, Twitter } from '@mui/icons-material';
import { Share as ShareIcon } from '@mui/icons-material';

import {
	userManagerPostMessages,
	userManagerGetAyrshareProfile,
	userManagerPostAyrshareProfile,
} from 'restful'; // TODO - docs
import ConfigureSocialMediaDialog from './ConfigureSocialMediaDialog';
import ShareToSocialMediaDialog from './ShareToSocialMediaDialog';
import SocialMediaSuccessDialog from './SocialMediaSuccessDialog';

const useStyles = makeStyles((theme) => ({
	backdropLoading: {
		zIndex: 999,
		position: 'fixed',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(0, 0, 0, 0.2)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	actionIconButton: {
		padding: 4,
		border: `1px solid`,
		color: 'inherit',
		'&:hover': {
			backgroundColor: `rgba(0,0,0,0.1)`,
		},
	},
	title: {
		margin: 0,
		padding: theme.spacing(2),
		cursor: 'move',
	},
	closeButton: {
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(1),
		color: theme.palette.grey[500],
	},
}));

const socialMediaIcons = {
	linkedin: <LinkedIn />,
	instagram: <Instagram />,
	facebook: <Facebook />,
	twitter: <Twitter />,
	tikTok: null,
};

const ayrSharePrimaryKey = config.ayrSharePrimaryKey.apiKey;

function ShareToSocialMedia({
	mediaFileValue,
	notifyGeneral,
	openGlobalDialog,
	userData,
	userId,
	domainSettings,
	variant = 'icon',
	menuText = 'Add to Screen Playlist',
	...rest
}) {
	const classes = useStyles();
	const intl = useIntl();

	const [message, setMessage] = useState('');
	const [scheduledDate, setScheduledDate] = useState(null);
	const [selectedAccounts, setSelectedAccounts] = useState([]);
	const [successDialogOpen, setSuccessDialogOpen] = useState(false);
	const [postResults, setPostResults] = useState([]);
	const [isPostingToSocialMedia, setSetIsPostingToSocialMedia] = useState(false);

	const [anchorAyrshareEl, setAnchorAyrshareEl] = React.useState(null);
	const [isSetupDialog, setIsSetupDialog] = React.useState(false);
	const [ayrshareProfileDetails, setAyrshareProfileDetails] = React.useState(null);
	const [isShareDialog, setIsShareDialog] = React.useState(false);
	const [isVideoMedia, setIsVideoMedia] = React.useState(false);

	// Close menu handler
	const handleMenuItemClose = () => {
		setAnchorAyrshareEl(null);
	};
	const handleGotoCustomArtwork = (mediafile) => {
		if (mediafile.isEsign) {
			window.location.assign(getRouteMediafileDetail(mediafile.id));
		} else {
			window.location.assign(getRouteArtworkCreator(mediafile.id));
		}
	};

	const handleAyrshareProfile = () => {
		userManagerGetAyrshareProfile({})
			.then((res) => {
				handleSuccess(res);
			})
			.catch(() => {
				userManagerPostAyrshareProfile({})
					.then((res) => {
						handleSuccess(res);
					})
					.catch((err) => {
						notifyGeneral(err.response ? err.response.data.message : err.message, 'error');
					});
			});
	};

	const handleSuccess = (res) => {
		setAyrshareProfileDetails(res.data);
		setAnchorAyrshareEl(null);
		if (res.data.activeSocials.length === 0) {
			setIsSetupDialog(true);
			setIsShareDialog(false);
		} else {
			setIsShareDialog(true);
			setIsSetupDialog(false);
		}
	};

	// Menu item click
	const handleShareToMediaButton = (mediafile, event) => {
		// Check if Ayrshare date has expired
		const isAyrshareExpired =
			!userData.ayrshareRenewalDate || new Date(userData.ayrshareRenewalDate) < new Date();
		if (isAyrshareExpired) {
			// Show premium feature message if expired
			openGlobalDialog({
				size: 'sm',
				title: intl.formatMessage({
					id: 'pages.MediaFiles.components.ShareToSocialMedia.ShareToMediaAyrshareExpTitle',
				}),
				content: (
					<div>
						<Typography variant="body1" gutterBottom>
							{intl.formatMessage({
								id:
									'pages.MediaFiles.components.ShareToSocialMedia.ShareToMediaAyrshareExpContent1',
							})}
							<Link
								style={{ cursor: 'pointer' }}
								underline="always"
								variant="inherit"
								href="mailto:support@visualid.com"
								rel="noreferrer"
								target="_blank"
							>
								{intl.formatMessage({
									id:
										'pages.MediaFiles.components.ShareToSocialMedia.ShareToMediaAyrshareExpContent2',
								})}
							</Link>
							{intl.formatMessage({
								id:
									'pages.MediaFiles.components.ShareToSocialMedia.ShareToMediaAyrshareExpContent3',
							})}
						</Typography>
					</div>
				),
			});
			return;
		}
		// Proceed normally if not expired
		if (mediafile.isArtworkTemplate) {
			openGlobalDialog({
				size: 'sm',
				title: intl.formatMessage({
					id: 'pages.MediaFiles.components.ShareToSocialMedia.ShareToMediaDialogTitle',
				}),
				content: intl.formatMessage({
					id: 'pages.MediaFiles.components.ShareToSocialMedia.ShareToMediaDialogContent',
				}),
				confirmCB: () => handleGotoCustomArtwork(mediafile),
			});
		} else {
			setAnchorAyrshareEl(event.currentTarget);
		}
	};

	const handleSocialMediaPopup = () => {
		isSetupDialog ? setIsSetupDialog(false) : setIsShareDialog(false);
		// Open the social media setup in a new window (popup)
		const width = window.outerWidth * 0.65;
		const height = window.outerHeight * 0.85;
		const left = (window.outerWidth - width) / 2;
		const top = (window.outerHeight - height) / 2;
		window.open(
			ayrshareProfileDetails.ssoUrl,
			'Choose Social Accounts',
			`scrollbars=yes,resizable=yes,location=no,width=${width},height=${height},left=${left},top=${top}`
		);
	};

	// Handles posting to Ayrshare
	const handlePost = async () => {
		if (message.trim() === '') {
			notifyGeneral('You need to enter a message to post.', 'error');
			return;
		}
		if (selectedAccounts.length === 0) {
			notifyGeneral('You need to select at least one social media account.', 'error');
			return;
		}
		setIsVideoMedia(mediaFileValue.fileType.toUpperCase() === 'VIDEO' ? true : false);

		const postObject = {
			post: message, // required
			platforms: selectedAccounts, // required
			mediaUrls:
				mediaFileValue.fileType.toUpperCase() === 'VIDEO'
					? [mediaFileValue.optimisedUrl]
					: [mediaFileValue.previewUrl], // optional
			youTubeOptions: {
				title: mediaFileValue.title, // required for YouTube
				youTubeVisibility: 'public', // optional: "public", "unlisted", or "private"
			},
			profileKeys: [ayrshareProfileDetails.profileKey],
		};

		if (scheduledDate) {
			postObject.scheduleDate = new Date(scheduledDate).toISOString().replace(/\.\d{3}Z$/, 'Z');
		}

		setSetIsPostingToSocialMedia(true);
		setIsShareDialog(false);

		try {
			const response = await fetch('https://app.ayrshare.com/api/post', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${ayrSharePrimaryKey}`,
				},
				body: JSON.stringify(postObject),
			});

			const result = await response.json();
			if (result.code) {
				notifyGeneral(
					'Sorry, but your post was unsuccessful. Please check your Social Media settings.',
					'error'
				);
			} else {
				handlePostResults(
					result.posts,
					mediaFileValue.fileType.toUpperCase() === 'VIDEO' ? true : false
				);
			}
		} catch (error) {
			notifyGeneral('An error occurred while posting. Please try again.', 'error');
		} finally {
			setSetIsPostingToSocialMedia(false);
		}
	};

	const handlePostResults = (posts, isVideo) => {
		const successPosts =
			posts?.[0]?.postIds?.map((post) => ({
				platform: post.platform,
				status: post.status,
				postUrl: post.postUrl || null, // Some platforms (like TikTok) don't return postUrl
			})) || [];

		const failedPosts =
			posts?.[0]?.errors?.map((error) => ({
				platform: error.platform,
				status: 'error',
				message: error.message,
			})) || [];

		const scheduledPosts =
			posts?.[0]?.status === 'scheduled'
				? selectedAccounts.map((platform) => ({
						platform,
						status: 'scheduled',
				  }))
				: [];

		const postDetails = [...successPosts, ...failedPosts, ...scheduledPosts];
		if (postDetails.length > 0) {
			setPostResults(postDetails);
			setSuccessDialogOpen(true);

			if (scheduledPosts.length > 0) {
				sendScheduledEmail(scheduledPosts);
			} else if (successPosts.length > 0) {
				sendSuccessEmail(successPosts, isVideo);
			}
		}
	};

	const sendSuccessEmail = (successPosts, isVideo) => {
		let emailContent = `Your post was successful.<br>Click the link below to view your post:<ul>`;
		let emailVideoContent = `Your post was successful. It may take 20 or 30 seconds before your video becomes available.<br>Click the link below to view your post:<ul>`;
		let emailToSend = isVideo ? emailVideoContent : emailContent;

		successPosts.forEach(({ platform, postUrl }) => {
			// const Icon = socialMediaIcons[platform]; // Use MUI Icon if available
			emailToSend += `<li><a href="${postUrl}" target="_blank">${formatAccountName(
				platform
			)}</a></li>`;
		});

		emailToSend += `</ul>`;

		sendEmail({
			subject: 'Your Social Media Post',
			content: emailToSend,
		});
	};

	const sendScheduledEmail = (scheduledPosts) => {
		let emailContent = `Your post has been scheduled successfully to the Social Media channels listed below.<br> You will receive a message once the post has been activated.<ul>`;

		scheduledPosts.forEach(({ platform }) => {
			emailContent += `<li>${formatAccountName(platform)}</li>`;
		});

		emailContent += `</ul>`;

		sendEmail({
			subject: 'You Scheduled a Social Media Post',
			content: emailContent,
		});
	};

	const sendEmail = ({ subject, content }) => {
		const mailObj = {
			senderUserId: '0',
			subject,
			content,
			recipientEmail: userData.email,
			recipientUserId: userId,
			senderName:
				domainSettings.settings.find((item) => item.key === 'domainToolkitName')?.value ?? '',
			senderEmail:
				domainSettings.settings.find((item) => item.key === 'toolkitEmailFrom')?.value ?? '',
			senderReplyTo:
				domainSettings.settings.find((item) => item.key === 'toolkitEmailTo')?.value ?? '',
		};
		userManagerPostMessages({ bodyParams: mailObj });
	};

	const formatAccountName = (account) => {
		if (account === 'tiktok') {
			return 'TikTok'; // tiktok should display as TikTok
		}
		if (account === 'linkedin') {
			return 'LinkedIn'; // linkedin should display as LinkedIn
		}
		return account.charAt(0).toUpperCase() + account.slice(1); // Capitalize only the first letter for other accounts
	};

	React.useEffect(() => {
		// Reset states when dialog opens
		if (isSetupDialog || isShareDialog) {
			setMessage('');
			setScheduledDate(null);
			setSelectedAccounts([]);
			setPostResults([]);
		}
	}, [isSetupDialog, isShareDialog]);

	const renderShareToSocialMediaTrigger = () => {
		if (variant === 'menuItem') {
			return (
				<Box
					onClick={(e) => handleShareToMediaButton(mediaFileValue, e)}
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						width: '100%',
						cursor: 'pointer',
					}}
				>
					{/* Icon with Text */}
					<IconButton
						aria-label="ShareToSocialMedia"
						className={classes.actionIconButton}
						onClick={(e) => handleShareToMediaButton(mediaFileValue, e)}
						title="Share to Social Media"
						size="small"
					>
						<ShareIcon fontSize="small" />
					</IconButton>

					<Typography
						variant="body2"
						sx={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							flex: 1,
						}}
					>
						{menuText}
					</Typography>
				</Box>
			);
		}

		// Default icon button
		return (
			<IconButton
				aria-label="ShareToSocialMedia"
				className={classes.actionIconButton}
				onClick={(e) => handleShareToMediaButton(mediaFileValue, e)}
				title="Share to Social Media"
				size="small"
			>
				<ShareIcon fontSize="small" />
			</IconButton>
		);
	};
	return (
		<>
			{renderShareToSocialMediaTrigger()}

			<Menu
				id="dropdown-menu"
				anchorEl={anchorAyrshareEl}
				open={Boolean(anchorAyrshareEl)}
				onClose={handleMenuItemClose}
			>
				<MenuItem onClick={() => handleAyrshareProfile()}>{userData.company}</MenuItem>
			</Menu>

			{/* Configure Social Media Dialog */}
			<ConfigureSocialMediaDialog
				open={isSetupDialog}
				onClose={() => setIsSetupDialog(false)}
				onProceed={handleSocialMediaPopup}
				intl={intl}
				classes={classes}
			/>

			{/* Share to Social Media Dialog */}
			<ShareToSocialMediaDialog
				isOpen={isShareDialog}
				onClose={() => setIsShareDialog(false)}
				message={message}
				setMessage={setMessage}
				setScheduledDate={setScheduledDate}
				selectedAccounts={selectedAccounts}
				setSelectedAccounts={setSelectedAccounts}
				ayrshareProfileDetails={ayrshareProfileDetails}
				socialMediaIcons={socialMediaIcons}
				formatAccountName={formatAccountName}
				mediaFileValue={mediaFileValue}
				handleSocialMediaPopup={handleSocialMediaPopup}
				handlePost={handlePost}
				setIsSetupDialog={setIsSetupDialog}
				intl={intl}
				classes={classes}
			/>

			{/* Success Dialog */}
			<SocialMediaSuccessDialog
				isOpen={successDialogOpen}
				onClose={() => setSuccessDialogOpen(false)}
				postResults={postResults}
				socialMediaIcons={socialMediaIcons}
				formatAccountName={formatAccountName}
				isVideoMedia={isVideoMedia}
				intl={intl}
			/>
			<Backdrop className={classes.backdropLoading} open={isPostingToSocialMedia}>
				<CircularProgress color="primary" size={65} thickness={5.5} />
			</Backdrop>
		</>
	);
}

ShareToSocialMedia.propTypes = {
	mediaFileValue: PropTypes.object,
	openGlobalDialog: PropTypes.func.isRequired,
	notifyGeneral: PropTypes.func.isRequired,
	userData: PropTypes.object.isRequired,
	userId: PropTypes.string.isRequired,
	domainSettings: PropTypes.object.isRequired,
	variant: PropTypes.string,
	menuText: PropTypes.string,
};

ShareToSocialMedia.defaultProps = {};

const mapStateToProps = () => {
	return {};
};

export default connect(mapStateToProps, {
	notifyGeneral,
	openGlobalDialog,
})(ShareToSocialMedia);
